import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Phone,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  ThumbsUp,
  Lightbulb,
  Clock,
  MessageSquare,
  Download,
  Share2,
  Copy,
  Check,
  Loader2,
  Home,
  Link as LinkIcon
} from "lucide-react";

export default function AnalysisResults() {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const resultsRef = useRef(null);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const response = await fetch(`${API}/analyze/${analysisId}`, {
          credentials: "include"
        });
        if (response.ok) {
          setResult(await response.json());
        } else {
          toast.error("Analysis not found");
          navigate("/history");
        }
      } catch (error) {
        console.error("Failed to fetch analysis:", error);
        toast.error("Failed to load analysis");
      } finally {
        setLoading(false);
      }
    };
    fetchAnalysis();
  }, [analysisId, navigate]);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const handleExportPDF = async () => {
    if (!resultsRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(resultsRef.current, {
        scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff"
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`call-analysis-${analysisId}.pdf`);
      toast.success("PDF exported successfully!");
    } catch (error) {
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  const handleCreateShareLink = async () => {
    setShareLoading(true);
    try {
      const response = await fetch(`${API}/analyze/${analysisId}/share`, {
        method: "POST", credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setShareLink(`${window.location.origin}/shared/analysis/${data.token}`);
        toast.success("Share link created!");
      } else {
        throw new Error("Failed");
      }
    } catch (error) {
      toast.error("Failed to create share link");
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-heading text-2xl font-bold mb-2">Analysis Not Found</h2>
        <p className="text-muted-foreground mb-6">This analysis could not be loaded.</p>
        <Link to="/history"><Button>View History</Button></Link>
      </div>
    );
  }

  const analysis = result.analysis;

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="analysis-results-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Badge variant="secondary" className="mb-2">Call Analysis</Badge>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">{result.filename}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExportPDF} disabled={exporting} data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export PDF
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="share-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Share Analysis</DialogTitle>
                <DialogDescription>Create a shareable link for this analysis.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                {!shareLink ? (
                  <Button onClick={handleCreateShareLink} className="w-full" disabled={shareLoading}>
                    {shareLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-2" />}
                    Generate Share Link
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Input value={shareLink} readOnly className="font-mono text-sm" />
                      <Button size="icon" variant="outline" onClick={handleCopyLink}>
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Anyone with this link can view the analysis.</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          <Link to="/analyze">
            <Button variant="outline" data-testid="new-analysis-btn">
              <Phone className="w-4 h-4 mr-2" /> New Analysis
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button data-testid="back-to-dashboard-btn">
              <Home className="w-4 h-4 mr-2" /> Dashboard
            </Button>
          </Link>
        </div>
      </div>

      {/* Results Content */}
      <div ref={resultsRef} className="space-y-6 bg-background p-4">
        {/* Score Overview */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="relative overflow-hidden" data-testid="main-score-card">
            <div className={`absolute top-0 left-0 right-0 h-1 ${
              (analysis.overall_score || result.score) >= 80 ? "bg-green-500" :
              (analysis.overall_score || result.score) >= 60 ? "bg-yellow-500" : "bg-red-500"
            }`} />
            <CardContent className="p-6 text-center">
              <CheckCircle className={`w-10 h-10 mx-auto mb-3 ${getScoreColor(analysis.overall_score || result.score)}`} />
              <p className={`font-heading text-5xl font-bold ${getScoreColor(analysis.overall_score || result.score)}`}>
                {analysis.overall_score || result.score}
              </p>
              <p className="text-muted-foreground mt-1">Overall Score</p>
            </CardContent>
          </Card>

          <Card className="md:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Analysis Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{analysis.summary}</p>
              <div className="flex gap-2 mt-4">
                {analysis.call_type && (
                  <Badge variant="secondary">
                    <Phone className="w-3 h-3 mr-1" />
                    {analysis.call_type.replace(/_/g, " ")}
                  </Badge>
                )}
                {analysis.duration_estimate && (
                  <Badge variant="outline">
                    <Clock className="w-3 h-3 mr-1" />
                    {analysis.duration_estimate}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Metrics */}
        {analysis.metrics && Object.keys(analysis.metrics).length > 0 && (
          <Card data-testid="metrics-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(analysis.metrics).map(([key, value]) => (
                  <div key={key} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium capitalize">{key.replace(/_/g, " ")}</span>
                      <span className={`font-mono font-bold ${getScoreColor(value)}`}>{value}%</span>
                    </div>
                    <Progress value={value} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Strengths & Improvements */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card data-testid="strengths-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-green-500">
                <ThumbsUp className="w-5 h-5" /> Strengths
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {analysis.strengths?.map((s, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                    <span className="text-sm">{s}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card data-testid="improvements-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-yellow-500">
                <Lightbulb className="w-5 h-5" /> Areas to Improve
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {analysis.areas_for_improvement?.map((a, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2 flex-shrink-0" />
                    <span className="text-sm">{a}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Transcript */}
        <Card data-testid="transcript-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" /> Transcript
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                {result.transcript}
              </p>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Recommendations */}
        {analysis.recommendations?.length > 0 && (
          <Card data-testid="recommendations-card">
            <CardHeader>
              <CardTitle className="text-lg">Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {analysis.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="text-sm">{rec}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
